import styled, { keyframes } from 'styled-components';

const float = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-16px) rotate(-0.6deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Hero = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  background: radial-gradient(
    circle farthest-corner at 12% 18%,
    #063d3d 0%,
    #021f26 55%,
    #09101c 100%
  );

  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 78% 45%, rgb(0 152 155 / 22%) 0%, transparent 55%);
    pointer-events: none;
  }
`;

export const TopBar = styled.header`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;

  @media (max-width: 768px) {
    padding: 28px 24px 12px;
    justify-content: center;
  }
`;

export const LogoZion = styled.img`
  height: 44px;
  cursor: pointer;
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  align-items: center;
  gap: 32px;
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 32px 80px;
  min-height: calc(100vh - 92px);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 24px 32px 72px;
    gap: 32px;
  }

  @media (max-width: 480px) {
    padding: 20px 28px 64px;
    gap: 28px;
  }
`;

export const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 28px;
  animation: ${fadeUp} 0.8s ease both;

  @media (max-width: 1024px) {
    align-items: center;
    gap: 28px;
  }

  @media (max-width: 480px) {
    gap: 26px;
  }
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid rgb(160 224 222 / 35%);
  border-radius: 999px;
  background: rgb(0 53 53 / 55%);
  color: #dafdb8;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

export const Headline = styled.h1`
  margin: 0;
  max-width: 12ch;
  color: #fff;
  font-weight: 300;
  font-size: clamp(2.25rem, 6vw, 5rem);
  line-height: 1.02;
  letter-spacing: -0.03em;

  strong {
    display: block;
    font-weight: 600;
    background: linear-gradient(90deg, #a0e0de 0%, #dafdb8 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  max-width: 46ch;
  color: rgb(255 255 255 / 72%);
  font-size: clamp(1rem, 1.4vw, 1.15rem);
  line-height: 1.6;
`;

export const StoreButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    width: 100%;
    flex-direction: column;
    gap: 14px;
  }
`;

export const StoreButton = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 26px;
  border-radius: 999px;
  border: 1px solid rgb(255 255 255 / 16%);
  background: rgb(255 255 255 / 6%);
  backdrop-filter: blur(8px);
  color: #fff;
  text-decoration: none;
  transition: 0.25s ease;

  svg {
    flex: none;
    font-size: 28px;
  }

  .store-label {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.25;
  }

  .store-label small {
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgb(255 255 255 / 65%);
  }

  .store-label span {
    font-size: 16px;
    font-weight: 700;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: rgb(218 253 184 / 55%);
    background: rgb(255 255 255 / 12%);
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    padding: 18px 26px;
  }
`;

export const Disclaimer = styled.span`
  color: rgb(255 255 255 / 45%);
  font-size: 13px;
`;

export const MockupWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    padding-top: 8px;
  }

  &:before {
    content: '';
    position: absolute;
    width: 78%;
    height: 78%;
    border-radius: 50%;
    background: radial-gradient(circle, rgb(0 152 155 / 45%) 0%, transparent 70%);
    filter: blur(60px);
  }
`;

export const Mockup = styled.img`
  position: relative;
  width: 100%;
  max-width: 520px;
  filter: drop-shadow(0 40px 80px rgb(0 0 0 / 55%));
  animation: ${float} 7s ease-in-out infinite;

  @media (max-width: 1024px) {
    max-width: 340px;
  }

  @media (max-width: 480px) {
    max-width: 280px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
